import React from 'react';
import AppShell from '../../../components/layout/AppShell';
import { Disclosure } from '@headlessui/react';
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/react/24/outline';
import {
  ShieldCheckIcon,
  LockClosedIcon,
  UserCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet';

const faqs = [
  {
    id: 1,
    question: "How do we protect your data?",
    answer: "We implement a variety of security measures to maintain the safety of your personal information when you enter, submit, or access your personal information.",
  },
  {
    id: 2,
    question: "What information do we collect?",
    answer: "We collect information from you when you register on our site, place an order, subscribe to our newsletter, or fill out a form.",
  },
  {
    id: 3,
    question: "Do we use cookies?",
    answer: "Yes, we use cookies to enhance your experience, gather general visitor information, and track visits to our website.",
  },
  {
    id: 4,
    question: "How can you manage your data?",
    answer: "You can update or delete your data at any time by logging into your account and accessing your profile settings.",
  }
];

const featuresSummary = [
  {
    name: 'Data Encryption',
    description: 'We use advanced encryption techniques to ensure your data is protected both during transmission and storage.',
    icon: LockClosedIcon,
    href: '#FeatureDetail_1',
  },
  {
    name: 'User Control',
    description: 'You have full control over your personal information, including access to update, delete, or restrict the processing of your data.',
    icon: UserCircleIcon,
    href: '#FeatureDetail_2',
  },
  {
    name: 'Transparent Policies',
    description: 'Our data handling processes are transparent, and we provide clear explanations on how your information is used.',
    icon: DocumentTextIcon,
    href: '#FeatureDetail_3',
  },
  {
    name: 'Third-Party Compliance',
    description: 'We ensure that any third parties we engage with adhere to strict data privacy standards.',
    icon: ShieldCheckIcon,
    href: '#FeatureDetail_4',
  }
];

const FeatureCard = ({ feature }) => (
  <div className="public-card public-card-body">
    <div className="flex items-center gap-3">
      <feature.icon className="h-6 w-6 text-primaryBlue" aria-hidden="true" />
      <h3 className="text-lg font-semibold text-darkGrey">{feature.name}</h3>
    </div>
    <p className="mt-4 text-sm leading-6 text-slateGrey">{feature.description}</p>
    <a href={feature.href} className="public-link mt-5 inline-flex items-center">
      Learn more
    </a>
  </div>
);

const FeatureDetail = ({ id, title, subtitle, description }) => (
  <section id={id} className="public-card public-card-body">
    <h2 className="text-2xl font-bold tracking-tight text-darkGrey">{title}</h2>
    <p className="mt-4 text-base leading-7 text-slateGrey">{description}</p>
  </section>
);

const DataPrivacy = () => {
  return (
    <AppShell currentPage="Data Privacy" loginRequired={false}>
      <Helmet>
        <title>CVHQ | Data Privacy</title>
        <meta
          name="description"
          content="Learn how CVHQ approaches data privacy, security, and transparency across the platform."
        />
        <meta
          name="keywords"
          content="CVHQ data privacy, personal information protection, user data control, transparent data policies, data encryption, third-party compliance"
        />
      </Helmet>

      <main className="public-page">
        <div id="top" className="public-page-shell public-section">
          <div className="public-page-hero">
            <h1 className="public-page-title">Data Privacy</h1>
            <p className="public-page-intro">
              CVHQ is designed to handle your information carefully, transparently, and with appropriate safeguards.
              This page outlines the principles behind our privacy and data handling approach.
            </p>
          </div>

          <div className="public-secondary-surface public-card-body mx-auto max-w-4xl">
            <h2 className="text-2xl font-bold tracking-tight text-darkGrey">Our commitment</h2>
            <p className="mt-4 text-base leading-7 text-slateGrey">
              We aim to give users confidence in how their information is managed. That includes clear explanations,
              practical safeguards, and straightforward access to the policies that govern the platform.
            </p>
          </div>

          <div className="public-grid mt-10 sm:grid-cols-2 xl:grid-cols-4">
            {featuresSummary.map((feature) => (
              <FeatureCard key={feature.name} feature={feature} />
            ))}
          </div>

          <div className="mt-10 space-y-6">
            {featuresSummary.map((feature, index) => (
              <FeatureDetail
                key={feature.name}
                id={`FeatureDetail_${index + 1}`}
                title={feature.name}
                subtitle={feature.name}
                description={feature.description}
              />
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center">
            <a href="#top" className="public-link">
              Back to top
            </a>
          </div>

          <div className="public-card public-card-body mt-12">
            <h2 className="text-2xl font-bold tracking-tight text-darkGrey">Frequently asked questions</h2>
            <dl className="mt-8 divide-y divide-gray-200">
              {faqs.map((faq) => (
                <Disclosure as="div" key={faq.question} className="py-6">
                  {({ open }) => (
                    <>
                      <dt>
                        <Disclosure.Button className="flex w-full items-start justify-between text-left text-darkGrey">
                          <span className="text-base font-semibold leading-7">{faq.question}</span>
                          <span className="ml-6 flex h-7 items-center">
                            {open ? (
                              <MinusSmallIcon className="h-6 w-6" aria-hidden="true" />
                            ) : (
                              <PlusSmallIcon className="h-6 w-6" aria-hidden="true" />
                            )}
                          </span>
                        </Disclosure.Button>
                      </dt>
                      <Disclosure.Panel as="dd" className="mt-2 pr-12">
                        <p className="text-base leading-7 text-slateGrey">{faq.answer}</p>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              ))}
            </dl>
          </div>
        </div>
      </main>
    </AppShell>
  );
};

export default DataPrivacy;



